import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { resolveBranding, mergeBranding } from "../lib/templates";
import { brandingCssVars } from "../lib/brandingCss";
import { loadGoogleFont } from "../lib/fonts";
import { BookingHeader } from "../components/BookingChrome";
import { Calendar as CalIcon, MapPin, Phone, ChevronLeft, ChevronRight } from "lucide-react";

function dateRange(start, end) {
  if (!start) return "Dates coming soon";
  const fmt = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); };
  return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
}

export default function PublicLandingPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/attraction/${slug}`);
        const evs = r.data.events || [];
        // Single published event → skip selection, go straight to its calendar
        if (evs.length === 1 && evs[0].slug) {
          nav(`/${slug}/${evs[0].slug}`, { replace: true });
          return;
        }
        setData(r.data);
      } catch { setErr("This attraction doesn't exist or isn't live yet."); }
    })();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <PublicShell><div className="text-center py-32 text-[#fca5a5]">{err}</div></PublicShell>;
  if (!data) return <PublicShell><div className="text-center py-32 text-[#8a8a8a]">Loading…</div></PublicShell>;

  const a = data.attraction;
  const events = data.events || [];
  const b = mergeBranding(a.branding);

  return (
    <PublicShell attraction={a}>
      <BookingHeader attraction={a} branding={b} />
      {(a.location || a.phone) && (
        <div className="px-6 md:px-12 pb-2 -mt-2">
          <div className="booking-header-inner !py-0 flex items-center gap-4 text-sm" style={{ color: "var(--text-color,#bbb)", opacity: 0.8 }}>
            {a.location && <span className="flex items-center gap-1"><MapPin size={14} /> {a.location}</span>}
            {a.phone && <span className="flex items-center gap-1"><Phone size={14} /> {a.phone}</span>}
          </div>
        </div>
      )}
      {!b.subtitle_text && !b.welcome_text && a.description && (
        <div className="px-6 md:px-12 pb-2">
          <p className="booking-header-inner !py-0 text-lg" style={{ color: "var(--text-color,#cfcfcf)", opacity: 0.85 }}>{a.description}</p>
        </div>
      )}

      <section className="px-6 md:px-12 pb-24 pt-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold flex items-center gap-2 mb-6" style={{ color: "var(--text-color,#fff)" }}>
            <CalIcon style={{ color: "var(--accent-color,#dc2626)" }} /> Choose your experience
          </h2>
          {events.length === 0 ? (
            <div className="pc-panel text-center text-[#8a8a8a] py-12" data-testid="landing-empty">No events on sale yet — check back soon.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="event-selection-grid">
              {events.map((e) => (
                <Link key={e.id} to={`/${slug}/${e.slug}`} data-testid={`event-card-${e.slug}`}
                      className="pc-panel group block transition-transform hover:-translate-y-1">
                  <div className="text-xl font-bold" style={{ color: "var(--text-color,#fff)" }}>{e.name}</div>
                  <div className="text-sm mt-1" style={{ color: "var(--accent-color,#dc2626)" }}>{dateRange(e.date_start, e.date_end)}</div>
                  {e.description && <p className="text-sm mt-3 line-clamp-3" style={{ color: "var(--text-color,#bbb)", opacity: 0.8 }}>{e.description}</p>}
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--accent-color,#dc2626)" }}>
                    Book now <ChevronRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

export function PublicShell({ children, attraction, hideLogo = false }) {
  const t = resolveBranding(attraction);
  const { vars, fonts } = brandingCssVars(attraction?.branding);
  const embed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "true";
  useEffect(() => { fonts.forEach(loadGoogleFont); }, [fonts.join(",")]); // eslint-disable-line
  const bdg = attraction?.branding || {};
  const bgVideo = bdg.bg_video_url;
  const bgPoster = bdg.bg_image_url || undefined;
  const bgOpacity = bdg.bg_image_opacity != null ? Math.max(0, Math.min(100, Number(bdg.bg_image_opacity))) / 100 : 0.8;
  const style = { backgroundColor: t.bg, color: t.text, ...vars };
  if (embed) { style["--bg-image"] = "none"; style["--bg-image-opacity-value"] = "0"; }
  return (
    <div
      className={`min-h-screen flex flex-col public-themed tpl-${t.templateId} ${embed ? "embed-mode" : ""}`}
      data-testid="public-shell"
      data-template={t.templateId}
      style={style}
    >
      {bgVideo && !embed && (
        <video key={bgVideo} className="bg-video-layer" autoPlay muted loop playsInline preload="auto"
               poster={bgPoster} style={{ opacity: bgOpacity }} data-testid="bg-video">
          <source src={bgVideo} />
        </video>
      )}
      {t.templateId === "asylum" && !embed && (
        <div className="asylum-band" data-testid="asylum-band">⚠ Restricted Access ⚠</div>
      )}
      <div className="booking-page-content flex-1" data-testid="booking-page-content">
        {!hideLogo && (
          <div className="logo-slot" data-testid="logo-slot">
            {t.logo && (
              <Link to={attraction ? `/${attraction.slug}` : "/"} data-testid="public-brand">
                <img src={t.logo} alt={attraction?.name || "logo"} className="attraction-logo" />
              </Link>
            )}
          </div>
        )}
        {children}
      </div>
      <footer className="booking-footer px-6 md:px-12 py-6 text-xs" style={{ color: "var(--text-color,#7a7a7a)", opacity: 1, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        Powered by <span className="st-brand text-base ml-1" style={{ color: "var(--text-color,#fff)" }}>SCARE<span className="text-[#dc2626]">TICKETS</span></span>
      </footer>
    </div>
  );
}

export function BackLink({ to, children }) {
  return <Link to={to} className="inline-flex items-center gap-1 st-link text-sm mb-4"><ChevronLeft size={14} />{children}</Link>;
}
